import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiImage, 
  FiLoader, 
  FiCheck,
  FiTag,
  FiX,
  FiSearch,
  FiRefreshCw,
  FiFolder,
  FiEye,
  FiCalendar,
  FiClock,
  FiMoreVertical,
  FiAlertTriangle
} from "react-icons/fi";
import { MdDelete, MdEdit } from "react-icons/md";
import { FaTrash, FaPencilAlt, FaEye } from "react-icons/fa";

const ManageCategories = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // add, edit, delete, view
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: "",
  });
  const [categoryImage, setCategoryImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || "https://api.dreamtoday.in";

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Filter categories based on search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [searchTerm, categories]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/admin/get-categories`, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        setCategories(response.data.categories);
        toast.success("Categories loaded successfully!");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error(error.response?.data?.message || "Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    setCategoryImage(file);

    if (imagePreview && !imagePreview.startsWith('http')) {
      URL.revokeObjectURL(imagePreview);
    }

    const preview = URL.createObjectURL(file);
    setImagePreview(preview);
  };

  const resetForm = () => {
    setFormData({ name: "" });
    setCategoryImage(null);
    if (imagePreview && !imagePreview.startsWith('http')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setSelectedCategory(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalMode("add");
    setShowModal(true);
  };

  const openViewModal = (category) => {
    setSelectedCategory(category);
    setModalMode("view");
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setSelectedCategory(category);
    setFormData({ name: category.name });
    
    if (category.image) {
      const imageUrl = category.image.startsWith('http') 
        ? category.image 
        : `${apiUrl}${category.image}`;
      setImagePreview(imageUrl);
    } else {
      setImagePreview(null);
    }
    
    setCategoryImage(null);
    setModalMode("edit");
    setShowModal(true);
  };

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setModalMode("delete");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => {
      resetForm();
    }, 300);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error("Category name is required");
      return false;
    }

    if (modalMode === "add" && !categoryImage) {
      toast.error("Category image is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      formDataToSend.append("name", formData.name);
      
      if (categoryImage) {
        formDataToSend.append("image", categoryImage);
      }

      let response;
      
      if (modalMode === "add") {
        response = await axios.post(
          `${apiUrl}/api/admin/create-category`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          toast.success("Category created successfully!");
          setCategories(prev => [...prev, response.data.category]);
          closeModal();
        }
      } else if (modalMode === "edit") {
        response = await axios.put(
          `${apiUrl}/api/admin/update-category/${selectedCategory._id}`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          toast.success("Category updated successfully!");
          setCategories(prev => 
            prev.map(cat => 
              cat._id === selectedCategory._id ? response.data.category : cat
            )
          );
          closeModal();
        }
      }
    } catch (error) {
      console.error("Error submitting category:", error);
      toast.error(error.response?.data?.message || `Failed to ${modalMode} category`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    
    try {
      const response = await axios.delete(
        `${apiUrl}/api/admin/delete-category/${selectedCategory._id}`,
        {
          withCredentials: true,
        }
      );

      if (response.data.success) {
        toast.success("Category deleted successfully!");
        setCategories(prev => prev.filter(cat => cat._id !== selectedCategory._id));
        closeModal();
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      
      // Check if error is because category has campaigns
      if (error.response?.data?.message?.includes("campaigns")) {
        toast.error("Cannot delete category because it has associated campaigns");
      } else {
        toast.error(error.response?.data?.message || "Failed to delete category");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${apiUrl}${imagePath}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
              <FiTag className="text-blue-500" />
              Manage Categories
            </h1>
            <p className="text-gray-600 mt-2 text-sm md:text-base">
              Create, edit, view, and delete campaign categories
            </p>
          </div>
          
          <button
            onClick={openAddModal}
            className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2 text-sm md:text-base shadow-lg hover:shadow-xl"
          >
            <FiPlus />
            Add New Category
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Categories Table */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 flex flex-col items-center justify-center">
            <FiLoader className="animate-spin text-4xl text-blue-500 mb-4" />
            <p className="text-gray-600">Loading categories...</p>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FiTag className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Categories Found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm ? "No categories match your search" : "Get started by creating your first category"}
            </p>
            {searchTerm ? (
              <button
                onClick={() => setSearchTerm("")}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all inline-flex items-center gap-2"
              >
                <FiRefreshCw />
                Clear Search
              </button>
            ) : (
              <button
                onClick={openAddModal}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all inline-flex items-center gap-2"
              >
                <FiPlus />
                Create Category
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Category Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Campaigns
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Created Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredCategories.map((category) => (
                    <tr key={category._id} className="hover:bg-gray-50 transition-all">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                            {category.image ? (
                              <img
                                src={getImageUrl(category.image)}
                                alt={category.name}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/48?text=No+Image";
                                }}
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <FiImage className="text-gray-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 capitalize">
                          {category.name}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          ID: {category._id.slice(-6)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FiFolder className={category.campaignCount > 0 ? "text-blue-500" : "text-gray-400"} />
                          <span className={`text-sm font-medium ${
                            category.campaignCount > 0 ? "text-blue-600" : "text-gray-500"
                          }`}>
                            {category.campaignCount}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FiCalendar className="text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {formatDate(category.createdAt)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {/* View Button */}
                          <button
                            onClick={() => openViewModal(category)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all"
                            title="View Category"
                          >
                            <FaEye size={18} />
                          </button>
                          
                          {/* Edit Button */}
                          <button
                            onClick={() => openEditModal(category)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            title="Edit Category"
                          >
                            <FaPencilAlt size={16} />
                          </button>
                          
                          {/* Delete Button - Always Enabled */}
                          <button
                            onClick={() => openDeleteModal(category)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title={category.campaignCount > 0 ? "Delete (Warning: Has campaigns)" : "Delete Category"}
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Table Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing {filteredCategories.length} of {categories.length} categories
              </p>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="p-4 md:p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="text-lg md:text-xl font-semibold text-gray-800">
                  {modalMode === "add" && "Add New Category"}
                  {modalMode === "edit" && "Edit Category"}
                  {modalMode === "delete" && "Delete Category"}
                  {modalMode === "view" && "Category Details"}
                </h2>
                <button
                  onClick={closeModal}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 transition-all flex items-center justify-center"
                >
                  <FiX />
                </button>
              </div>

              {/* Modal Body */}
              {modalMode === "view" && selectedCategory && (
                <div className="p-4 md:p-6">
                  <div className="mb-6 flex justify-center">
                    {selectedCategory.image ? (
                      <img
                        src={getImageUrl(selectedCategory.image)}
                        alt={selectedCategory.name}
                        className="w-48 h-48 object-cover rounded-lg border-4 border-gray-200 shadow-lg"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/400x400?text=No+Image";
                        }}
                      />
                    ) : (
                      <div className="w-48 h-48 bg-gray-100 rounded-lg border-4 border-gray-200 flex items-center justify-center">
                        <FiImage className="text-6xl text-gray-300" />
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="border-b border-gray-200 pb-3">
                      <label className="text-sm font-medium text-gray-500">Category Name</label>
                      <p className="text-lg font-semibold text-gray-800 capitalize">{selectedCategory.name}</p>
                    </div>

                    <div className="border-b border-gray-200 pb-3">
                      <label className="text-sm font-medium text-gray-500">Category ID</label>
                      <p className="text-sm text-gray-600 font-mono">{selectedCategory._id}</p>
                    </div>

                    <div className="border-b border-gray-200 pb-3">
                      <label className="text-sm font-medium text-gray-500">Campaigns Count</label>
                      <div className="flex items-center gap-2 mt-1">
                        <FiFolder className={selectedCategory.campaignCount > 0 ? "text-blue-500" : "text-gray-400"} />
                        <p className={`text-base font-semibold ${selectedCategory.campaignCount > 0 ? "text-blue-600" : "text-gray-600"}`}>
                          {selectedCategory.campaignCount} {selectedCategory.campaignCount === 1 ? 'Campaign' : 'Campaigns'}
                        </p>
                      </div>
                    </div>

                    <div className="border-b border-gray-200 pb-3">
                      <label className="text-sm font-medium text-gray-500">Created Date</label>
                      <div className="flex items-center gap-2 mt-1">
                        <FiCalendar className="text-gray-400" />
                        <p className="text-sm text-gray-600">{formatDate(selectedCategory.createdAt)}</p>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">Last Updated</label>
                      <div className="flex items-center gap-2 mt-1">
                        <FiClock className="text-gray-400" />
                        <p className="text-sm text-gray-600">{formatDate(selectedCategory.updatedAt || selectedCategory.createdAt)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              {modalMode === "delete" && (
                <div className="p-4 md:p-6">
                  {selectedCategory?.campaignCount > 0 ? (
                    <>
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <FiAlertTriangle className="h-5 w-5 text-yellow-400" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                              <span className="font-bold">Warning:</span> This category has {selectedCategory.campaignCount} active campaign(s). 
                              Deleting it may affect those campaigns.
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-6">
                        Are you sure you want to delete the category "<span className="font-semibold">{selectedCategory?.name}</span>"? This action cannot be undone.
                      </p>
                      
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={closeModal}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleDelete}
                          disabled={submitting}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {submitting ? (
                            <>
                              <FiLoader className="animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <MdDelete size={18} />
                              Delete Anyway
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-600 mb-6">
                        Are you sure you want to delete the category "<span className="font-semibold">{selectedCategory?.name}</span>"? This action cannot be undone.
                      </p>
                      
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={closeModal}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleDelete}
                          disabled={submitting}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {submitting ? (
                            <>
                              <FiLoader className="animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <MdDelete size={18} />
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Enter category name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center gap-1">
                        <FiImage className="text-orange-500" />
                        Category Image {modalMode === "add" ? "*" : "(Leave empty to keep current)"}
                      </div>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-3 md:px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      required={modalMode === "add"}
                    />
                  </div>

                  {imagePreview && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600 mb-2">Preview:</p>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                      />
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4 sticky bottom-0 bg-white">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <FiLoader className="animate-spin" />
                          {modalMode === "add" ? "Creating..." : "Updating..."}
                        </>
                      ) : (
                        <>
                          <FiCheck />
                          {modalMode === "add" ? "Create" : "Update"}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageCategories;